import React, { useEffect, useState } from 'react';
import { GlobalDropdown, GlobalDropdownOption } from './GlobalDropdown';
import { GlobalButton } from './GlobalButton';
import { useLocale } from '../context/DynamicLocaleProvider';
import { getActiveBrand, setActiveBrand } from '../utils/brandSwitcher';
import styles from './InvitationModal.module.css';

interface BrandSwitchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BrandSwitchModal({ isOpen, onClose }: BrandSwitchModalProps) {
  const { t } = useLocale();
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const BRANDS: GlobalDropdownOption[] = [
    { value: '', label: 'Thescon' },
    { value: 'nuernberger', label: 'Nürnberger Versicherung' },
    { value: 'allianz', label: 'Allianz' },
  ];

  useEffect(() => {
    if (isOpen) {
      setSelectedBrand(getActiveBrand());
      setSaving(false);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (selectedBrand === undefined) {
      return;
    }
    setSaving(true);
    try {
      setActiveBrand(selectedBrand);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const isDifferentFromCurrent = selectedBrand !== getActiveBrand();

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>

        {/* Header */}
        <div className={styles.header}>
          <h2>{t('brand_switch_modal_title')}</h2>
          <button onClick={onClose} className={styles.closeButton}>
            {t('invitation_modal_close_button')}
          </button>
        </div>

        {/* Form */}
        <form className={styles.form} onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <GlobalDropdown
            label={t('brand_switch_modal_label')}
            value={selectedBrand}
            onChange={setSelectedBrand}
            options={BRANDS}
            placeholder={t('brand_switch_modal_placeholder')}
            required
          />
        </form>

        {/* Actions */}
        <div className={styles.actions}>
          <GlobalButton
            type="button"
            onClick={onClose}
            disabled={saving}
            variant="secondary"
          >
            {t('invitation_modal_cancel_button')}
          </GlobalButton>
          <GlobalButton
            type="button"
            onClick={handleSave}
            isLoading={saving}
            disabled={saving || selectedBrand === undefined || !isDifferentFromCurrent}
          >
            {t('brand_switch_modal_apply_button')}
          </GlobalButton>
        </div>

      </div>
    </div>
  );
}

